import React, { useEffect, useState } from 'react'
import { Dialog, Button, Input } from 'components/ui'
import PropTypes from 'prop-types'
import {
    HiOutlineClipboardCopy,
    HiOutlineExclamationCircle,
} from 'react-icons/hi'

const InviteLinkDialog = ({ isOpen, onClose, inviteUrl, loading }) => {
    const [copyStatus, setCopyStatus] = useState(null) // null, 'success', 'denied'
    const [permissionChecked, setPermissionChecked] = useState(false)

    useEffect(() => {
        if (isOpen && inviteUrl && !permissionChecked) {
            // Try to copy automatically when dialog opens
            attemptCopy()
            setPermissionChecked(true)
        }
    }, [isOpen, inviteUrl])

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setCopyStatus(null)
            setPermissionChecked(false)
        }
    }, [isOpen])

    const attemptCopy = async () => {
        if (!inviteUrl) return

        try {
            // Check if clipboard API is available
            if (!navigator.clipboard) {
                setCopyStatus('denied')
                return
            }

            // Try to copy to clipboard
            await navigator.clipboard.writeText(inviteUrl)
            setCopyStatus('success')
        } catch (error) {
            // Permission denied or other error
            setCopyStatus('denied')
        }
    }

    const handleManualCopy = () => {
        attemptCopy()
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            width={600}
            style={{ marginTop: 150 }}
        >
            <div className="px-6 py-4">
                <h5 className="mb-4">Invite Link</h5>

                {loading && (
                    <div className="text-center py-4">
                        <p>Loading invite link...</p>
                    </div>
                )}

                {!loading && inviteUrl && (
                    <>
                        <p>
                            For security reasons, a new invite link will be
                            generated every time this action is done.
                        </p>
                        <div className="mb-4">
                            <Input
                                value={inviteUrl}
                                readOnly
                                onClick={(e) => e.target.select()}
                                className="font-mono text-sm"
                            />
                        </div>

                        {copyStatus === 'success' && (
                            <div className="flex items-center gap-2 p-3 mb-4 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-100 rounded">
                                <HiOutlineClipboardCopy className="text-xl" />
                                <span>Link copied to clipboard!</span>
                            </div>
                        )}

                        {copyStatus === 'denied' && (
                            <div className="flex items-center gap-2 p-3 mb-4 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100 rounded">
                                <HiOutlineExclamationCircle className="text-xl" />
                                <span>
                                    Permission to copy to clipboard is denied.
                                    Please copy the link manually.
                                </span>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button
                                size="sm"
                                variant="solid"
                                onClick={handleManualCopy}
                                icon={<HiOutlineClipboardCopy />}
                            >
                                Copy Link
                            </Button>
                            <Button size="sm" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </>
                )}

                {!loading && !inviteUrl && (
                    <div className="text-center py-4">
                        <p className="text-red-600">
                            Unable to retrieve invite link for this supplier.
                        </p>
                    </div>
                )}
            </div>
        </Dialog>
    )
}

InviteLinkDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    inviteUrl: PropTypes.string,
    loading: PropTypes.bool,
}

export default InviteLinkDialog
